import { Suspense, lazy } from "react";
import { Toaster, SonnerToaster, TooltipProvider } from "@lyder-talk/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, createQueryClient } from "@lyder-talk/core";

import NotFound from "./pages/NotFound";

// Lazy load admin pages
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminSessionsPage = lazy(() => import("./pages/AdminSessionsPage"));
const AdminCommentsPage = lazy(() => import("./pages/AdminCommentsPage"));
const AdminStudyGroupsPage = lazy(() => import("./pages/AdminStudyGroupsPage"));
const AdminCategoriesPage = lazy(() => import("./pages/AdminCategoriesPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
const AdminSettingsPage = lazy(() => import("./pages/AdminSettingsPage"));
const AdminAIRoutingPage = lazy(() => import("./pages/AdminAIRoutingPage"));
const AdminActivityLogPage = lazy(() => import("./pages/AdminActivityLogPage"));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage"));
const AdminVideosPage = lazy(() => import("./pages/AdminVideosPage"));
const AdminVideoRequestsPage = lazy(() => import("./pages/AdminVideoRequestsPage"));
const AdminYouTubeImportPage = lazy(() => import("./pages/AdminYouTubeImportPage"));
const AdminBlogsPage = lazy(() => import("./pages/AdminBlogsPage"));
const Auth = lazy(() => import("./pages/Auth"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = createQueryClient();

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <SonnerToaster />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/:mode" element={<Auth />} />
                  <Route path="/" element={<AdminDashboardPage />} />
                  <Route path="/users" element={<AdminUsersPage />} />
                  <Route path="/sessions" element={<AdminSessionsPage />} />
                  <Route path="/comments" element={<AdminCommentsPage />} />
                  <Route path="/study-groups" element={<AdminStudyGroupsPage />} />
                  <Route path="/categories" element={<AdminCategoriesPage />} />
                  <Route path="/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="/reports" element={<AdminReportsPage />} />
                   <Route path="/settings" element={<AdminSettingsPage />} />
                   <Route path="/ai-routing" element={<AdminAIRoutingPage />} />
                   <Route path="/ai" element={<Navigate to="/ai-routing" replace />} />
                  <Route path="/activity-log" element={<AdminActivityLogPage />} />
                  <Route path="/videos" element={<AdminVideosPage />} />
                  <Route path="/video-requests" element={<AdminVideoRequestsPage />} />
                  <Route path="/youtube-transcripts" element={<AdminYouTubeImportPage />} />
                  <Route path="/blogs" element={<AdminBlogsPage />} />
                  <Route path="/admin/*" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
